<script setup lang="ts">
    import { computed, useTemplateRef } from 'vue';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { createSavedQuery, updateSavedQuery, SavedQuerySaveRequest } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper.js';
    import useShow from '@/shared/composables/useShow.js';
    import useBuefy from '@/shared/composables/useBuefy';
    import { useRouter } from 'vue-router';
    import { getSqlEditorLinkBySavedQuery } from '@/shared/helpers/routing';

    const { showInfo, showError } = useShow();
    const buefy = useBuefy();
    const store = useSqlEditorStore();
    const saveButtonDialog = useTemplateRef<any>('saveButtonDialog');
    const router = useRouter();
    const serviceNowUrl = window.config?.reportErrorUrl;

    const publicSwitchEnabled = computed(() =>
        store.canSave
        && (store.userIsAdministrator || store.loadedQueryOriginalIsPublic));

    async function save() {
        if (!store.canSave)
            return;

        try {
            if (store.queryIsLoaded) {
                await updateSavedQuery(http, store.loadedQueryId!, store.loadedQueryGetSaveRequest());
                showInfo('Query saved');
                store.loadedQuerySetCurrentOriginal();
                await store.savedQueriesReload();
                await router.push(getSqlEditorLinkBySavedQuery(store.loadedQueryGuid!));
            } else {
                saveNewQuery(store.loadedQueryGetSaveRequest());
            }
            close();
        } catch (error) {
            showError('Unable to save ' + error);
        }
    }

    async function saveAs() {
        if (!store.queryIsLoaded)
            return;

        const request = store.loadedQueryGetSaveRequest();
        close();

        buefy.dialog.prompt({
            message: 'New query name:',
            inputAttrs: {
                placeholder: request.name,
                minlength: 1,
                maxlength: store.maxQueryNameLength,
                required: true
            },
            trapFocus: true,
            onConfirm: async (newName: string) => {
                try {
                    request.name = newName.trim();
                    request.isPublic = false; // Copies are never public
                    saveNewQuery(request);
                } catch (error) {
                    showError('Unable to save copy: ' + error);
                }
            }
        });
    }

    async function saveNewQuery(request: SavedQuerySaveRequest) {
        const response = await createSavedQuery(http, request);
        showInfo('New query saved');
        await store.savedQueriesReload();
        await router.push(getSqlEditorLinkBySavedQuery(response.data.guid));
    }

    function close() {
        saveButtonDialog.value.toggle();
    }
</script>

<template>
    <b-dropdown
        ref="saveButtonDialog"
        position="is-bottom-right"
        append-to-body
        aria-role="dialog"
        trap-focus>
        <template #trigger>
            <button
                class="button"
                role="button">
                <b-icon
                    icon="floppy"
                    size="is-small" />
                <span>Save</span>
            </button>
        </template>

        <b-dropdown-item
            aria-role="listitem"
            :focusable="false"
            custom
            paddingless>
            <div
                class="card"
                style="width:400px;">
                <section class="card-content is-size-6">
                    <b-field
                        label="Query name">
                        <input
                            v-model="store.loadedQueryName"
                            :class="store.loadedQueryName.length > store.maxQueryNameLength ? 'input is-danger': 'input'">
                        <template #message>
                            <p
                                v-if="store.loadedQueryName.length > store.maxQueryNameLength"
                                class="help is-danger">
                                Query name should be shorter than {{ store.maxQueryNameLength }} characters.
                            </p>
                            <p class="is-info">
                                Use one or more forward slashes in the name to create folders.
                                E.g. the name <u>Projects/Johan Sverdrup/Electrical cables</u> will create a folder for <i>Projects</i>,
                                a subfolder for <i>Johan Sverdrup</i>, and a query called <i>Electrical cables</i>.
                            </p>
                        </template>
                    </b-field>
                    <b-field
                        label="Page size"
                        message="The number of rows to show on each page when doing pagination of the result.">
                        <b-numberinput
                            v-model="store.loadedQueryPageSize"
                            min="1"
                            max="1000"
                            step="1"
                            controls-alignment="right"
                            controls-position="compact" />
                    </b-field>
                    <b-field
                        label="Shared">
                        <b-switch
                            v-model="store.loadedQueryIsShared">
                            Shared queries can be found by other users.
                        </b-switch>
                    </b-field>
                    <b-field
                        label="Public">
                        <b-switch
                            v-model="store.loadedQueryIsPublic"
                            :disabled="!publicSwitchEnabled">
                            <b>Important:</b> If the saved query is to be run using the API, the query must be made Public.
                            Only <u>administrators</u> can set a saved query Public.
                            <br>
                            <a
                                :href="serviceNowUrl"
                                target="_blank">Make a request in ServiceNow</a>.
                        </b-switch>
                    </b-field>
                </section>
                <footer class="card-footer">
                    <div class="card-footer-item is-justify-content-end">
                        <b-field grouped>
                            <button
                                class="button"
                                title="Cancel"
                                @click="close">
                                <span>Cancel</span>
                            </button>
                            <button
                                class="button"
                                title="Save a copy with a different name"
                                :disabled="!store.queryIsLoaded"
                                @click="saveAs">
                                <b-icon
                                    icon="floppy"
                                    size="is-small" />
                                <span>Save as..</span>
                            </button>
                            <button
                                class="button is-primary"
                                title="Save query"
                                :disabled="!store.canSave"
                                @click="save">
                                <b-icon
                                    icon="floppy"
                                    size="is-small" />
                                <span>Save</span>
                            </button>
                        </b-field>
                    </div>
                </footer>
            </div>
        </b-dropdown-item>
    </b-dropdown>
</template>
